import { useContext, useState } from 'react'

import {
  convertConditionToMediaActionListVars,
  useMediaActionSearchCondition,
  useMediaActionSummaryApi,
  useMediaActionsListApi,
  useUpdateMediaActionMutation
} from '@/common/api/modules/mediaAction'
import { useFirstLoading } from '@/common/api/search'
import {
  ActionTabKey,
  MEDIA_ACTION_VIEW_TABS,
  MediaActionViewTabKey
} from '@/common/app/modules/media/mediaAction'
import { useSiteId } from '@/common/app/site'
import { MediaActionStatus } from '@/common/model/media/mediaAction'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Tab from '@/components/view/atoms/tab/Tab'
import TabList from '@/components/view/atoms/tab/TabList'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import { MessageType } from '@/components/view/molecules/bar/MessageBar'
import PageListLoading from '@/components/view/molecules/loading/PageListLoading'
import PageListSearching from '@/components/view/molecules/loading/PageListSearching'
import NoResultGuidance from '@/components/view/molecules/search/NoResultGuidance'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import AppTitleBadge, { AppTitleBadgeType } from '@/components/container/layout/AppTitleBadge'
import ActionDetailModalProvider from '@/components/container/modal/automationReview/ActionDetailModalProvider'
import AutoScheduleModalProvider from '@/components/container/modal/automationReview/AutoScheduleModalProvider'
import DecomposeResultModalProvider from '@/components/container/modal/automationReview/DecomposeResultModalProvider'
import ExecLogModalProvider from '@/components/container/modal/automationReview/ExecLogModalProvider'
import MediaActionListTable from '@/components/container/modules/media/automationReview/MediaActionListTable'
import MediaActionRoadmapView from '@/components/container/modules/media/automationReview/MediaActionRoadmapView'
import MediaActionSearchConditionField from '@/components/container/modules/media/automationReview/MediaActionSearchConditionField'
import { ToastMessageContext } from '@/components/container/utils/message/ToastMessageProvider'

// 推奨アクション管理（一覧／ロードマップ）
const MediaActionPageInner = () => {
  const siteId = useSiteId()
  const { addMessage } = useContext(ToastMessageContext)

  // 検索条件（上位タブ・状態フィルタ・絞り込み・並び順）。URLクエリで保持
  const [condition, setCondition, onUpdatePage] = useMediaActionSearchCondition()
  const view = condition.view ?? 'list'
  const statusFilter = condition.status ?? 'suggested'

  // 選択モード（提案中タブ）
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  // 上位タブ・状態フィルタの切替時は選択状態・ページをリセットする
  const changeView = (next: MediaActionViewTabKey) => {
    setCondition((c) => ({ ...c, view: next, page: 1 }))
    setSelectedIds([])
  }
  const changeStatus = (next: ActionTabKey) => {
    setCondition((c) => ({ ...c, status: next, page: 1 }))
    setSelectedIds([])
  }

  const {
    data: listData,
    isLoading,
    error: listError,
    mutate: mutateList
  } = useMediaActionsListApi(convertConditionToMediaActionListVars(siteId, condition))
  const { data: summaryData, mutate: mutateSummary } = useMediaActionSummaryApi({ site_id: siteId })
  const isFirstLoading = useFirstLoading(isLoading)

  const counts = summaryData?.counts ?? {}
  const items = (listData?.items ?? []).filter((a) =>
    // ロードマップは提案中・進行中のみ表示
    view === 'roadmap' ? a.status === 'suggested' || a.status === 'in_progress' : true
  )

  const refetchAll = () => {
    mutateList()
    mutateSummary()
  }

  // ステータス更新
  const { trigger: updateAction } = useUpdateMediaActionMutation({ onSuccess: () => refetchAll() })
  const updateStatus = (
    id: number,
    status: MediaActionStatus,
    extra?: { rejection_note?: string }
  ) => {
    updateAction({ site_id: siteId, action_id: id, status, ...extra }).catch(() => undefined)
  }

  // 選択された複数アクションに連番（1, 2, 3, ...）を付与
  const { trigger: updateOrder } = useUpdateMediaActionMutation()
  const applyOrderToSelected = async () => {
    if (selectedIds.length === 0) return
    try {
      await Promise.all(
        selectedIds.map((id, i) =>
          updateOrder({ site_id: siteId, action_id: id, execution_order: i + 1 })
        )
      )
      addMessage({
        text: `${selectedIds.length} 件の実行順を設定しました。`,
        type: MessageType.SUCCESS
      })
      setSelectedIds([])
      refetchAll()
    } catch {
      // エラーはトーストで通知済み
    }
  }

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='推奨アクション管理'
        badgeNode={<AppTitleBadge type={AppTitleBadgeType.MEDIA} />}
        description='各分析（コンテンツギャップ・カニバリ・内部リンク・サイト調査）から提案された推奨アクションの実行状況を管理します。'
      />

      <Content>
        {/* 上位タブ：一覧／ロードマップ */}
        <Area>
          <TabList>
            {MEDIA_ACTION_VIEW_TABS.map((t) => (
              <Tab key={t.key} selected={view === t.key} onClick={() => changeView(t.key)}>
                {t.label}
              </Tab>
            ))}
          </TabList>
        </Area>

        {/* データ取得エラー */}
        {listError && (
          <Area bgColor='caution'>
            <AreaContent>
              <div className='flex flex-wrap items-center justify-between gap-3'>
                <Text size={12} color={TextColor.CRITICAL}>
                  データの取得に失敗しました。ネットワーク接続をご確認のうえ再読込してください。
                </Text>
                <Button color={ButtonColor.SECONDARY} onClick={refetchAll}>
                  再試行
                </Button>
              </div>
            </AreaContent>
          </Area>
        )}

        {/* コンテンツ */}
        {isFirstLoading ? (
          <PageListLoading />
        ) : view === 'roadmap' ? (
          <MediaActionRoadmapView siteId={siteId} items={items} onChanged={refetchAll} />
        ) : (
          <Area tableWrap noBorder>
            <MediaActionSearchConditionField
              condition={condition}
              setCondition={setCondition}
              onChangeStatus={changeStatus}
              counts={counts}
              selectedIds={selectedIds}
              onClearSelected={() => setSelectedIds([])}
              onApplyOrderToSelected={applyOrderToSelected}
            />
            {isLoading ? (
              <PageListSearching />
            ) : items.length > 0 ? (
              <MediaActionListTable
                siteId={siteId}
                items={items}
                tab={statusFilter}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onSelectAll={setSelectedIds}
                onUpdateStatus={updateStatus}
                onChanged={refetchAll}
                condition={condition}
                onUpdatePage={onUpdatePage}
                total={listData?.total ?? 0}
              />
            ) : (
              <NoResultGuidance searched name='推奨アクション' />
            )}
          </Area>
        )}
      </Content>
    </Main>
  )
}

const MediaActionPage = () => (
  <ActionDetailModalProvider>
    <ExecLogModalProvider>
      <AutoScheduleModalProvider>
        <DecomposeResultModalProvider>
          <MediaActionPageInner />
        </DecomposeResultModalProvider>
      </AutoScheduleModalProvider>
    </ExecLogModalProvider>
  </ActionDetailModalProvider>
)

export default MediaActionPage
